<template>
  <section>
    <div class="w-full flex flex-wrap items-center justify-between mb-6 mt-3">
      <h2>Public Program Page</h2>
    </div>
    <p class="text-gray-700 mb-6">
      Show your program's results on commutrics.com. The page lists your employer name and
      program-wide totals only: logged trips, alternative-mode share, CO2 and vehicle miles saved,
      commuter savings, trees equivalent and the yearly mode split. No individual commuter data is
      shared.
    </p>

    <form @submit.prevent="handleSubmit">
      <div class="flex flex-col gap-3">
        <CustumLabel
          name="join_code"
          label="Page address"
          labelTooltip="Lowercase letters, numbers and hyphens, 2 to 40 characters"
          :error="errors.join_code"
        >
          <Input
            v-model:value="displayJoinCode"
            placeholder="e.g. acme-corp"
            :maxlength="40"
            @blur="joinCode = normalizeJoinCode(joinCode)"
          />
        </CustumLabel>

        <CustumLabel name="public_impacts" :error="errors.public_impacts">
          <div class="flex items-center gap-3">
            <Switch v-model:checked="publicImpacts" :disabled="!normalizeJoinCode(joinCode)" />
            <span class="text-black">Show my program's impact figures publicly</span>
          </div>
        </CustumLabel>
      </div>

      <div class="mt-6">
        <Button
          type="primary"
          html-type="submit"
          class="btn-gradient rounded"
          size="large"
          block
          :loading="loading"
          :disabled="!isChanged"
          >Save</Button
        >
      </div>
    </form>
  </section>
</template>

<script>
import { Button, Input, Switch, message } from 'ant-design-vue';
import CustumLabel from '@/components/shared/form/CustumLabel.vue';
import authApi from '@/services/apis/auth.api';
import { AUTH } from '@/store/types';
import handleResErrors from '@/utils/handleResErrors';

// Same normalisation as the backend: page addresses are stored lowercase.
const normalizeJoinCode = (val) => (val || '').trim().toLowerCase();

export default {
  components: {
    Button,
    Input,
    Switch,
    CustumLabel,
  },
  data() {
    return {
      joinCode: '',
      publicImpacts: false,
      errors: {},
      loading: false,
    };
  },
  computed: {
    user() {
      return this.$store.state.auth.user || null;
    },
    // Display only: the first letter is shown capitalized, joinCode itself stays lowercase.
    displayJoinCode: {
      get() {
        return this.joinCode.charAt(0).toUpperCase() + this.joinCode.slice(1);
      },
      set(val) {
        this.joinCode = val.charAt(0).toLowerCase() + val.slice(1);
      },
    },
    isChanged() {
      return (
        normalizeJoinCode(this.joinCode) !== (this.user?.join_code || '')
        || this.publicImpacts !== !!this.user?.public_impacts
      );
    },
  },
  watch: {
    user: {
      immediate: true,
      handler() {
        this.joinCode = this.user?.join_code || '';
        this.publicImpacts = !!this.user?.public_impacts;
      },
    },
    joinCode(val) {
      // The page is served by its address, so it can't stay public without one.
      if (!normalizeJoinCode(val)) {
        this.publicImpacts = false;
      }
    },
  },
  methods: {
    normalizeJoinCode,
    async handleSubmit() {
      if (!this.isChanged) {
        return;
      }
      this.loading = true;
      this.errors = {};

      try {
        await authApi.updateUser({
          join_code: normalizeJoinCode(this.joinCode) || null,
          public_impacts: this.publicImpacts,
        });
        const data = await authApi.getUser();
        this.$store.commit(AUTH.SET_USER_DATA, data);
        message.success('Updated successfully');
      } catch (error) {
        const errors = error.response?.data?.errors || {};
        this.errors = Object.fromEntries(
          Object.entries(errors).map(([key, val]) => [key, Array.isArray(val) ? val[0] : val]),
        );
        message.error(handleResErrors(error), 10);
      }
      this.loading = false;
    },
  },
};
</script>
